import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { subscriptionFormActionsV2 } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormV2Sagas'
import { selectPceSearchData } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { markPceSearchFailed } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import usePceSearchV2 from './usePceSearchV2'
import {
  GRDF_UNAVAILABLE_BLOCK_SECONDS,
  OCTOPUS_GRDF_UNAVAILABLE,
  OCTOPUS_LIMIT_REACHED,
  OCTOPUS_LIMIT_REACHED_BLOCK_SECONDS,
  PCE_SEARCH_GRDF_NOT_COVERING,
  PCE_SEARCH_RATE_LIMITED,
  failureReasonOf,
  searchedParams,
} from './pceSearchRules'

const searchBlockFor = (reason, seconds) => ({
  reason,
  until: new Date(Date.now() + seconds * 1000).toISOString(),
  totalSeconds: seconds,
})

const useRetrievePCEsV2 = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { errorMessage, podSearchDataId } = useSelector(selectPceSearchData)

  const {
    getStreetNumberName,
    getZipCode,
    getInseeCode,
    getCity,
    getSerialNumber,
    pceSearchParams,
    updateList,
    updatePceConfigData,
  } = usePceSearchV2()

  const blockSearch = (reason, seconds) => {
    updatePceConfigData({ isLoading: false, searchBlock: searchBlockFor(reason, seconds) })
  }

  const handleSearchError = errorCode => {
    if (errorCode === OCTOPUS_GRDF_UNAVAILABLE) {
      blockSearch('grdf_unavailable', GRDF_UNAVAILABLE_BLOCK_SECONDS)
      return
    }

    if (errorCode === OCTOPUS_LIMIT_REACHED) {
      blockSearch('rate_limited', OCTOPUS_LIMIT_REACHED_BLOCK_SECONDS)
      return
    }

    const reason = failureReasonOf(errorCode)
    if (reason !== null) {
      dispatch(markPceSearchFailed({ reason }))
    }
    if (reason === null || reason === 'already_failed') {
      updatePceConfigData({
        errorMessage: t('subscriptionTools:pce_search.default_error_message'),
        isLoading: false,
        viewPage: 'results',
      })
    }
  }

  const searchPCEs = () => {
    const searchParams = {
      streetName: getStreetNumberName,
      streetNumber: pceSearchParams.streetNumber,
      zipcode: getZipCode,
      inseeCode: getInseeCode,
      city: getCity,
      occupant: searchedParams(pceSearchParams).occupant,
      serialNumber: getSerialNumber,
    }

    const onSuccess = (results, searchId = null) => {
      updatePceConfigData({ errorMessage: null })
      if (results && Object.keys(results).length > 0) {
        updateList([results]) // there is always one result for PCE search
        updatePceConfigData({ viewPage: 'results', isLoading: false })
      } else if (searchId) {
        updatePceConfigData({ podSearchDataId: searchId })
        // Will be handled by broadcast listener
      }
    }

    const onError = (_errorMessage, errorCode = null) => {
      handleSearchError(errorCode)
    }

    const onBlocked = (blockCode, retryAfter) => {
      if (blockCode === PCE_SEARCH_GRDF_NOT_COVERING) {
        dispatch(markPceSearchFailed({ reason: 'grdf_not_covering' }))
        return
      }

      const reason = blockCode === PCE_SEARCH_RATE_LIMITED ? 'rate_limited' : 'grdf_unavailable'
      blockSearch(reason, retryAfter || GRDF_UNAVAILABLE_BLOCK_SECONDS)
    }

    updatePceConfigData({ isLoading: true, pendingSearchParams: pceSearchParams })
    updateList([])

    dispatch({
      type: subscriptionFormActionsV2.PCE_SEARCH_V2,
      searchParams,
      onSuccess,
      onError,
      onBlocked,
    })
  }

  useEffect(() => {
    if (podSearchDataId === null) {
      return
    }

    window.Echo.channel(`retrieve-search-pod-data.${podSearchDataId}`).listen('.retrieve-search-pod-data', e => {
      if (e.error_message !== null) {
        handleSearchError(e.error_code ?? null)
        return
      }

      if (e.response && Object.keys(e.response).length > 0) {
        updateList([e.response])
      }

      updatePceConfigData({ isLoading: false, viewPage: 'results' })
    })

    return () => {
      window.Echo.leaveChannel(`retrieve-search-pod-data.${podSearchDataId}`)
    }
  }, [podSearchDataId])

  return {
    searchPCEs,
    errorMessage,
  }
}

export default useRetrievePCEsV2
